// User preferences for the web app: appearance, formats, behaviour and the
// dashboards. They are kept in localStorage (instant, also before sign-in) and
// synced to the server (GET/PUT /api/user/preferences) so they follow the user
// to other devices. Server-side settings that the iOS app shares (work week,
// rounding, notifications) are NOT here: see stores/account.svelte.ts.

import { api } from './api';
import { i18n, type Lang } from './i18n.svelte';

export type Theme = 'light' | 'dark' | 'system';
export type Accent = 'blue' | 'indigo' | 'violet' | 'sky' | 'teal' | 'green' | 'orange' | 'rose' | 'slate';
export type DurationFormat = 'hm' | 'decimal' | 'text';
export type StartPage = 'dashboard' | 'timer' | 'entries' | 'clients' | 'reports';

export interface WidgetConfig {
  id: string;
  type: string;
  /** Width in columns of a 12-column grid (3, 4, 6, 8 or 12). */
  w: number;
  /** Height in grid rows (1–4). */
  h: number;
  title?: string;
  options?: Record<string, unknown>;
}

export interface Dashboard {
  id: string;
  name: string;
  icon?: string;
  widgets: WidgetConfig[];
}

export interface Prefs {
  theme: Theme;
  accent: Accent;
  density: 'compact' | 'comfortable' | 'spacious';
  radius: 'sharp' | 'default' | 'round';
  sidebarStyle: 'light' | 'brand';
  sidebarCollapsed: boolean;
  fontScale: number;
  reduceMotion: boolean;
  language: Lang | null;

  dateFormat: 'DD-MM-YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
  timeFormat: '24h' | '12h';
  durationFormat: DurationFormat;
  currency: 'EUR' | 'USD' | 'GBP' | 'CHF';

  startPage: StartPage;
  clientPrefillMode: 'off' | 'remember' | 'default';
  defaultClientId: number | null;
  lastUsedClientId: number | null;
  defaultRate: number | null;
  defaultBillable: boolean;
  showDependencyWarnings: boolean;
  confirmDeletes: boolean;
  timerInTitle: boolean;
  showSeconds: boolean;
  keyboardShortcuts: boolean;
  showClientsInSidebar: boolean;
  showWeekNumbers: boolean;

  entriesView: 'list' | 'grouped';
  entriesColumns: string[];
  entriesPageSize: number;
  reportsPeriod: string;

  dashboards: Dashboard[];
  activeDashboardId: string | null;

  tourCompleted: boolean;
  dismissed: Record<string, string | number | boolean>;
  /** Per-client UI settings (target line, forecast planner …). */
  clientUi: Record<string, Record<string, unknown>>;
}

export const DEFAULT_PREFS: Prefs = {
  theme: 'system',
  accent: 'blue',
  density: 'comfortable',
  radius: 'default',
  sidebarStyle: 'light',
  sidebarCollapsed: false,
  fontScale: 1,
  reduceMotion: false,
  language: null,

  dateFormat: 'DD-MM-YYYY',
  timeFormat: '24h',
  durationFormat: 'hm',
  currency: 'EUR',

  startPage: 'dashboard',
  clientPrefillMode: 'remember',
  defaultClientId: null,
  lastUsedClientId: null,
  defaultRate: null,
  defaultBillable: true,
  showDependencyWarnings: true,
  confirmDeletes: true,
  timerInTitle: true,
  showSeconds: true,
  keyboardShortcuts: true,
  showClientsInSidebar: true,
  showWeekNumbers: false,

  entriesView: 'list',
  entriesColumns: ['date', 'time', 'client', 'activity', 'description', 'duration', 'rate', 'amount', 'status'],
  entriesPageSize: 50,
  reportsPeriod: 'month',

  dashboards: [],
  activeDashboardId: null,

  tourCompleted: false,
  dismissed: {},
  clientUi: {}
};

const STORAGE_KEY = 'tp.prefs';
/** Set while a local change has not been saved to the server yet. */
const DIRTY_KEY = 'tp.prefs.dirty';

function readLocal(): Partial<Prefs> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeLocal(value: Prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch { /* private mode / quota: the server copy still works */ }
}

class PrefsStore {
  value = $state<Prefs>({ ...DEFAULT_PREFS, ...readLocal() });
  loadedFromServer = $state(false);
  saving = $state(false);
  private timer: ReturnType<typeof setTimeout> | null = null;
  private media: MediaQueryList | null = null;

  constructor() {
    if (typeof window === 'undefined') return;
    this.media = window.matchMedia('(prefers-color-scheme: dark)');
    this.media.addEventListener('change', () => this.apply());
    if (this.value.language) i18n.set(this.value.language);
    this.apply();
    // A change made just before leaving the page must still reach the server.
    window.addEventListener('pagehide', () => this.flushOnExit());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushOnExit();
    });
  }

  private flushOnExit() {
    if (!this.timer || !this.signedIn) return;
    clearTimeout(this.timer);
    this.timer = null;
    try {
      fetch('/api/user/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ preferences: $state.snapshot(this.value) })
      }).then((res) => res.ok && localStorage.removeItem(DIRTY_KEY)).catch(() => {});
    } catch { /* body too large for keepalive: the dirty flag makes the next load save it */ }
  }

  /** Change one or more preferences; applied at once, saved shortly after. */
  update(patch: Partial<Prefs>) {
    this.value = { ...this.value, ...patch };
    writeLocal(this.value);
    if (this.signedIn) {
      try { localStorage.setItem(DIRTY_KEY, String(Date.now())); } catch { /* ignore */ }
    }
    if ('language' in patch && patch.language) i18n.set(patch.language);
    this.apply();
    this.scheduleSave();
  }

  /** Reset appearance-related preferences only. */
  resetAppearance() {
    const { theme, accent, density, radius, sidebarStyle, fontScale, reduceMotion } = DEFAULT_PREFS;
    this.update({ theme, accent, density, radius, sidebarStyle, fontScale, reduceMotion });
  }

  async load() {
    this.signedIn = true;
    try {
      const res = await api.get<{ preferences: Partial<Prefs> }>('/api/user/preferences');
      const server = res.preferences || {};
      if (Object.keys(server).length === 0 || localStorage.getItem(DIRTY_KEY)) {
        // First time on this account, or changes here that never reached the
        // server: this browser's copy is newer, store it.
        this.scheduleSave(0);
      } else {
        this.value = { ...DEFAULT_PREFS, ...server };
        writeLocal(this.value);
        if (this.value.language) i18n.set(this.value.language);
        this.apply();
      }
      this.loadedFromServer = true;
    } catch {
      // Offline or server busy: the local copy is used until the next load.
    }
  }

  /** Set by the session: preferences only go to the server while signed in. */
  signedIn = false;

  private scheduleSave(delay = 800) {
    if (!this.signedIn) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(), delay);
  }

  async save() {
    this.timer = null;
    this.saving = true;
    try {
      await api.put('/api/user/preferences', { preferences: $state.snapshot(this.value) });
      try { localStorage.removeItem(DIRTY_KEY); } catch { /* ignore */ }
    } catch (err) {
      // Retry later (not after a 401: signed out); the local copy is already safe.
      if ((err as { status?: number })?.status !== 401 && this.signedIn) this.timer = setTimeout(() => this.save(), 15000);
    } finally {
      this.saving = false;
    }
  }

  get resolvedTheme(): 'light' | 'dark' {
    if (this.value.theme === 'system') return this.media?.matches ? 'dark' : 'light';
    return this.value.theme;
  }

  apply() {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const v = this.value;
    root.setAttribute('data-theme', this.resolvedTheme);
    const attr = (name: string, value: string | null) => (value ? root.setAttribute(name, value) : root.removeAttribute(name));
    attr('data-accent', v.accent !== 'blue' ? v.accent : null);
    attr('data-density', v.density !== 'comfortable' ? v.density : null);
    attr('data-radius', v.radius !== 'default' ? v.radius : null);
    attr('data-sidebar', v.sidebarStyle === 'brand' ? 'brand' : null);
    attr('data-motion', v.reduceMotion ? 'reduced' : null);
    root.style.setProperty('--font-scale', String(v.fontScale || 1));
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', this.resolvedTheme === 'dark' ? '#0a0e17' : '#ffffff');
  }
}

export const prefs = new PrefsStore();
